/**
 * Resolves a pending deal cost: the deal closed (book it against the posted
 * investment) or broke (write it off on the day it was called off). The parent
 * owns the mutation.
 */

import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDealCostInvestments } from '@/hooks/react-query/useDealCosts';
import {
  DEAL_COST_OUTCOME_LABELS,
  type DealCost,
  type DealCostInvestment,
  type DealCostResolve,
} from '@/types/deal-costs';
import { formatDate, formatReferenceType } from '@/utils/formatters';

import {
  hasAnyError,
  isResolvableOn,
  validateResolveDealCost,
  type ResolveDealCostErrors,
  type ResolveDealCostValues,
  type ResolveOutcome,
} from './resolveDealCostValidators';

const RESOLVE_OUTCOMES: ResolveOutcome[] = ['CLOSED', 'ABORTED'];

const EMPTY_VALUES: ResolveDealCostValues = {
  outcome: 'CLOSED',
  investment_fund_transaction_id: '',
  aborted_on: '',
};

function investmentLabel(investment: DealCostInvestment): string {
  const name =
    investment.description ??
    `${investment.portfolio_company_name} - ${formatReferenceType(
      investment.transaction_type.toLowerCase()
    )}`;
  return `${name} - ${formatDate(investment.transaction_date)}`;
}

export interface ResolveDealCostDialogProps {
  cost: DealCost | null;
  fundId: string;
  isPending: boolean;
  onResolve: (costId: string, data: DealCostResolve) => Promise<void>;
  onClose: () => void;
}

export function ResolveDealCostDialog({
  cost,
  fundId,
  isPending,
  onResolve,
  onClose,
}: ResolveDealCostDialogProps) {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [errors, setErrors] = useState<ResolveDealCostErrors>({});
  const { data: investments, isLoading } = useDealCostInvestments(
    fundId,
    cost !== null
  );
  const investmentsAfterPayment = (investments ?? []).filter(
    (investment) =>
      cost !== null &&
      isResolvableOn(investment.transaction_date, cost.cost_date)
  );

  const close = () => {
    setValues(EMPTY_VALUES);
    setErrors({});
    onClose();
  };

  const submit = async () => {
    if (!cost) return;
    const validationErrors = validateResolveDealCost(values, cost.cost_date);
    setErrors(validationErrors);
    if (hasAnyError(validationErrors)) return;
    await onResolve(
      cost.id,
      values.outcome === 'CLOSED'
        ? {
            outcome: 'CLOSED',
            investment_fund_transaction_id:
              values.investment_fund_transaction_id,
          }
        : { outcome: 'ABORTED', aborted_on: values.aborted_on }
    );
    close();
  };

  return (
    <Dialog open={cost !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resolve Deal Cost</DialogTitle>
          <DialogDescription>
            {cost?.deal_name}: say whether the deal closed or broke. A posted
            cost moves to where the outcome puts it on that date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="resolve-outcome">Outcome</Label>
            <Select
              value={values.outcome}
              onValueChange={(value) =>
                setValues({ ...values, outcome: value as ResolveOutcome })
              }
            >
              <SelectTrigger id="resolve-outcome">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOLVE_OUTCOMES.map((outcome) => (
                  <SelectItem key={outcome} value={outcome}>
                    {DEAL_COST_OUTCOME_LABELS[outcome]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {values.outcome === 'CLOSED' ? (
            <div className="space-y-2">
              <Label htmlFor="resolve-investment">Investment</Label>
              <Select
                value={values.investment_fund_transaction_id}
                onValueChange={(value) =>
                  setValues({
                    ...values,
                    investment_fund_transaction_id: value,
                  })
                }
              >
                <SelectTrigger id="resolve-investment">
                  <SelectValue
                    placeholder={
                      isLoading ? 'Loading investments…' : 'Select investment'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {investmentsAfterPayment.map((investment) => (
                    <SelectItem key={investment.id} value={investment.id}>
                      {investmentLabel(investment)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isLoading && investmentsAfterPayment.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No posted investment dated on or after the payment.
                </p>
              )}
              {errors.investment_fund_transaction_id && (
                <p className="text-sm text-destructive">
                  {errors.investment_fund_transaction_id}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="resolve-aborted-on">Called off on</Label>
              <DateInput
                id="resolve-aborted-on"
                value={values.aborted_on}
                onChange={(value) =>
                  setValues({ ...values, aborted_on: value })
                }
              />
              {errors.aborted_on && (
                <p className="text-sm text-destructive">{errors.aborted_on}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending ? 'Resolving…' : 'Resolve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
